import React, { useCallback, useState, useEffect } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Package, Trash2 } from "lucide-react";
import { PackageBuilder } from "./PackageBuilder";

/**
 * Decimal-friendly discount input. Keeps an internal raw text state so the
 * user can type `0`, `0.`, `0,9`, `,5`, etc. without React clobbering the
 * leading zero or the in-progress decimal separator. The numeric value
 * is propagated upwards via `onChange(number)`. Empty string is treated as 0.
 *
 * Accepts both `,` and `.` as decimal separators (Spanish locale).
 */
const DiscountInput = ({ value, onChange, dataTestId }) => {
  // Format a numeric value into the canonical text we display when the
  // input is NOT being edited. We keep dots internally so existing styling
  // and `type="text" inputmode="decimal"` keep working consistently.
  const numToText = (n) => {
    if (n === null || n === undefined || n === 0 || Number.isNaN(n)) return "";
    return String(n);
  };
  const [text, setText] = useState(numToText(value));

  // Re-sync local text when the parent value changes from outside (e.g. when
  // PackageBuilder regenerates services and resets descuento to 0). We avoid
  // overriding the user while they are typing a partial decimal like "0,".
  useEffect(() => {
    const parsed = parseFloat(String(text).replace(",", "."));
    const numeric = Number.isFinite(parsed) ? parsed : 0;
    if (numeric !== (value || 0)) {
      setText(numToText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    let raw = e.target.value;
    // Allow only digits, comma and dot. Strip everything else so paste of
    // currency text ("12,50 €") still produces a valid number.
    raw = raw.replace(/[^\d.,]/g, "");
    setText(raw);
    const parsed = parseFloat(raw.replace(",", "."));
    onChange(Number.isFinite(parsed) ? parsed : 0);
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      placeholder="0"
      data-testid={dataTestId}
      className="w-20 h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-center text-sm"
    />
  );
};

export const ServicesSection = ({ newOpp, setNewOpp, catalogServices, formatCurrency, onValidationChange }) => {
  // Shared formula used everywhere (bottom table, estimated_mrr, PackageSummary):
  //   line_total = (precio * cantidad) - descuento  (clamped to 0 for positive-price lines)
  //   For catalog discount lines (precio < 0), descuento is NOT applied again.
  const lineTotal = (svc) => {
    const qty = parseInt(svc.cantidad) || 1;
    const base = (svc.precio || 0) * qty;
    const dto = svc.descuento || 0;
    if ((svc.precio || 0) < 0) return base; // negative-price discount lines stay as-is
    return Math.max(0, base - dto);
  };
  const calculateMRRWithDiscount = (services) =>
    services.reduce((total, svc) => total + lineTotal(svc), 0);

  const handleServicesSync = useCallback((newServices) => {
    setNewOpp(prev => {
      const merged = newServices.map(svc => {
        const match = prev.selected_services.find(e =>
          (svc.linea_id && e.linea_id === svc.linea_id) ||
          (e.catalog_service_id === svc.catalog_service_id &&
           e.bloque === svc.bloque &&
           e.linea_numero === svc.linea_numero)
        );
        return { ...svc, descuento: match ? (match.descuento || 0) : 0 };
      });
      // estimated_mrr must include manual discounts (same formula as the
      // bottom table and the PackageSummary).
      const mrr = merged.reduce((s, v) => {
        const qty = parseInt(v.cantidad) || 1;
        const base = (v.precio || 0) * qty;
        const dto = v.descuento || 0;
        return s + ((v.precio || 0) < 0 ? base : Math.max(0, base - dto));
      }, 0);
      return { ...prev, selected_services: merged, estimated_mrr: mrr };
    });
  }, [setNewOpp]);

  // Dictionary keyed by linea_id so PackageSummary can show the discounted
  // totals without owning the discount state itself.
  const manualDiscountsByLineaId = newOpp.selected_services.reduce((acc, s) => {
    if (s.linea_id && (s.descuento || 0) > 0) acc[s.linea_id] = s.descuento;
    return acc;
  }, {});

  return (
    <div className="space-y-4 pt-4 border-t border-[#1E1E5F] mt-4">
      <h3 className="text-lg font-semibold text-[#03BEE4] flex items-center gap-2">
        <Package className="h-5 w-5" />
        Servicios a Contratar
      </h3>

      <PackageBuilder
        catalog={catalogServices}
        onAddToQuote={handleServicesSync}
        formatCurrency={formatCurrency}
        onBuilderStateChange={(_hasBlocks, validation) => {
          if (onValidationChange) onValidationChange(validation || { ok: true, violations: [] });
        }}
        customerId={newOpp.customer_id}
        manualDiscounts={manualDiscountsByLineaId}
      />

      {/* Selected services — discount editing */}
      {newOpp.selected_services.length > 0 && (
        <div className="space-y-2">
          <Label className="text-slate-300">Servicios seleccionados:</Label>
          {newOpp.selected_services.map((svc, index) => (
            <div key={index} className="flex items-center justify-between bg-[#1B1B4B] p-3 rounded-lg">
              <div className="flex-1 min-w-0">
                <span className="text-white font-medium">{svc.nombre}</span>
                {svc.bloque && <span className="text-slate-500 text-xs ml-2">({svc.bloque.replace(/_/g, " ")})</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-slate-400 text-sm">x{svc.cantidad || 1}</span>
                <div className="flex items-center gap-1">
                  <Label className="text-slate-400 text-xs">Dto €:</Label>
                  <DiscountInput
                    value={svc.descuento}
                    onChange={(val) => {
                      const updated = [...newOpp.selected_services];
                      updated[index] = { ...updated[index], descuento: val };
                      setNewOpp({ ...newOpp, selected_services: updated });
                    }}
                    dataTestId={`discount-input-${index}`}
                  />
                </div>
                <span className="text-[#42E695] font-semibold min-w-[90px] text-right">
                  {formatCurrency(Math.max(0, (svc.precio * (parseInt(svc.cantidad) || 1)) - (svc.descuento || 0)))}/mes
                </span>
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-2 border-t border-[#1E1E5F]">
            <span className="text-lg">
              <span className="text-slate-400">MRR Total:</span>
              <span className="text-[#42E695] font-bold ml-2">{formatCurrency(calculateMRRWithDiscount(newOpp.selected_services))}/mes</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
